import { useCallback, useMemo, useState } from 'react';
import { Button, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus, ShieldCheck, ShieldOff } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import ConfirmActionModal from '../components/member-detail/ConfirmActionModal';
import Can from '../components/guards/Can';
import AssignItemModal from '../components/member-tabs/AssignItemModal';
import MemberRoleCard from '../components/member-tabs/MemberRoleCard';
import MemberTabEmpty from '../components/member-tabs/MemberTabEmpty';
import MemberTabGuard from '../components/member-tabs/MemberTabGuard';
import { MEMBER_STATUS, USER_TYPE } from '../constants/copy';
import { PERMISSIONS } from '../constants/permissions';
import { useMemberRoles } from '../hooks/useMemberRoles';
import { getFirstName } from '../utils/memberDisplay';

/**
 * Roles tab of the member detail: the roles they have (who gave them and when, Edit role and
 * Remove), Assign role and the confirm before removing one. Admins and Owners get everything by
 * their user type, so the tab says so instead of listing roles.
 *
 * @param {object} props
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 * @param {string} props.basePath Where the host mounted People & Access (links to the role page).
 */
function MemberRolesTab({ member, basePath }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { roles, assignable, isLoading, error, assign, remove, reload } = useMemberRoles(member);
  const [isAssigning, setIsAssigning] = useState(false);
  const [removing, setRemoving] = useState(null);

  const isPrivileged = member.userType === USER_TYPE.ADMIN || member.userType === USER_TYPE.OWNER;

  const handleOpenAssign = useCallback(() => setIsAssigning(true), []);
  const handleCloseAssign = useCallback(() => setIsAssigning(false), []);
  const handleCloseRemove = useCallback(() => setRemoving(null), []);
  const handleEdit = useCallback(
    (roleId) => navigate(`${basePath}/roles/${roleId}`, { state: { backTo: pathname } }),
    [navigate, basePath, pathname],
  );
  const handleConfirmRemove = useCallback(() => remove(removing.id), [remove, removing]);

  const items = useMemo(
    () =>
      assignable.map((role) => ({
        id: role.id,
        name: role.name,
        description: role.description,
        badge: role.isSystem ? t('memberTabs.roles.system') : null,
      })),
    [assignable, t],
  );

  return (
    <MemberTabGuard
      tabId="roles"
      icon={ShieldOff}
      isRemoved={member.status === MEMBER_STATUS.REMOVED}
      isLoading={isLoading}
      error={error}
      onRetry={reload}
    >
      {isPrivileged ? (
        <StatusMessage
          icon={ShieldCheck}
          title={t('memberTabs.roles.privilegedTitle')}
          subtitle={t('memberTabs.roles.privilegedSubtitle')}
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5 text-sm">
              <span className="text-content-secondary">
                {t('memberTabs.roles.countAssigned', { count: roles.length })}
              </span>
              <span className="text-xs text-content-muted">
                {t('memberTabs.roles.hint', { name: getFirstName(member.displayName) })}
              </span>
            </div>
            <Can permission={PERMISSIONS.MEMBERS_MANAGE_ACCESS}>
              <Button variant="secondary" size="sm" leftIcon={Plus} onClick={handleOpenAssign}>
                {t('memberTabs.roles.assign')}
              </Button>
            </Can>
          </div>
          {roles.length === 0 ? (
            <MemberTabEmpty
              icon={ShieldOff}
              title={t('memberTabs.roles.emptyTitle')}
              subtitle={t('memberTabs.roles.emptySubtitle')}
            />
          ) : (
            <div className="flex flex-col gap-2">
              {roles.map((role) => (
                <MemberRoleCard key={role.id} role={role} onEdit={handleEdit} onRemove={setRemoving} />
              ))}
            </div>
          )}
        </div>
      )}
      <AssignItemModal
        isOpen={isAssigning}
        title={t('memberTabs.roles.assignModal.title')}
        subtitle={t('memberTabs.roles.assignModal.subtitle', { name: member.displayName })}
        searchPlaceholder={t('memberTabs.roles.assignModal.searchPlaceholder')}
        emptyMessage={t('memberTabs.roles.assignModal.empty')}
        noMatchMessage={t('memberTabs.roles.assignModal.noMatch')}
        items={items}
        onClose={handleCloseAssign}
        onPick={assign}
      />
      <ConfirmActionModal
        isOpen={removing !== null}
        onClose={handleCloseRemove}
        onConfirm={handleConfirmRemove}
        title={t('memberTabs.roles.removeConfirm.title', { role: removing?.name })}
        message={t('memberTabs.roles.removeConfirm.message', { name: member.displayName })}
        confirmLabel={t('memberTabs.roles.removeConfirm.confirm')}
        confirmVariant="danger"
        icon={ShieldCheck}
        iconTone="Alert"
      />
    </MemberTabGuard>
  );
}

export default MemberRolesTab;
